// The environment selector of the LiveOps pages (spec/dashboard.md section 8): content is per branch, but segment sizes,
// A/B results and revenue are per environment, so the pages of section 8 share one chosen environment (dev by default,
// remembered per game).
import { useCallback, useEffect, useState } from 'react';
import type { Environment } from '../../api/types';
import { useGame } from '../../shell/GameContext';
import { Tabs } from '../../ui/Tabs';

const KEY = 'hue.liveops.env';

function read(gameId: string): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const map = JSON.parse(raw) as Record<string, string>;
    return map[gameId] ?? null;
  } catch {
    return null;
  }
}

function write(gameId: string, slug: string): void {
  try {
    const raw = localStorage.getItem(KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    map[gameId] = slug;
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // a private window: the choice lives for the session only
  }
}

const listeners = new Set<() => void>();

/** The environment the LiveOps figures are read from: the remembered one, else dev, else the first. */
export function useLiveOpsEnv(): { env: Environment | null; environments: Environment[]; setEnv: (slug: string) => void } {
  const { game, environments } = useGame();
  const [slug, setSlug] = useState<string | null>(() => read(game.id));
  useEffect(() => {
    const l = () => setSlug(read(game.id));
    listeners.add(l);
    return () => void listeners.delete(l);
  }, [game.id]);
  const env = environments.find((e) => e.slug === slug) ?? environments.find((e) => e.kind === 'dev') ?? environments[0] ?? null;
  const setEnv = useCallback(
    (next: string) => {
      write(game.id, next);
      for (const l of listeners) l();
    },
    [game.id],
  );
  return { env, environments, setEnv };
}

/**
 * The selector itself: "Figures of" and a segmented control of the game's environments, with the help every page of
 * section 8 shows; the choice is the shared `useLiveOpsEnv` one (remembered per game).
 */
export function EnvSelector({ label = 'Figures of' }: { label?: string }) {
  const { env, environments, setEnv } = useLiveOpsEnv();
  if (environments.length === 0) return null;
  return (
    <span className="env-selector lo-env-seg" title="Content is per branch; the players' figures (segment sizes, A/B results, revenue) are per environment.">
      {label && <span className="env-selector-label">{label}</span>}
      <Tabs segmented ariaLabel="Environment of the figures" value={env?.slug ?? ''} onChange={setEnv} items={environments.map((e) => ({ key: e.slug, label: e.name ?? e.slug }))} />
    </span>
  );
}
